import { useCallback, useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Mail } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner } from "@/components/Bits";
import Overview from "@/pages/admin/Overview";
import LeaguesAdmin from "@/pages/admin/LeaguesAdmin";
import Moderation from "@/pages/admin/Moderation";
import Ops from "@/pages/admin/Ops";
import CorporateAdmin from "@/pages/admin/CorporateAdmin";
import RegieAdmin from "@/pages/admin/regie/RegieAdmin";
import OrganizationsAdmin from "@/pages/admin/OrganizationsAdmin";
import SiteAdmin from "@/pages/admin/SiteAdmin";
import ExperiencesAdmin from "@/pages/admin/ExperiencesAdmin";
import AppearanceAdmin from "@/pages/admin/AppearanceAdmin";

const TABS = [["", "Vue d'ensemble"], ["ligues", "Ligues"], ["entreprises", "Ligues d'entreprise"], ["organisations", "Organisations"], ["contenu", "Contenu du site"], ["apparence", "Apparence"], ["experiences", "Experiences"], ["regie", "Régie commerciale"], ["moderation", "Signalements et journal"], ["exploitation", "Courriels, sauvegardes, paramètres"]];

function MfaGate({ onDone }) {
  const { user } = useAuth();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/superadmin/mfa/start"); setSent(true); toast.success("Code envoyé par courriel"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/superadmin/mfa/verify", { code }); onDone(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section className="mpl-card max-w-md space-y-4 p-6" data-testid="admin-mfa-gate">
      <p className="flex items-center gap-2 text-base font-bold"><ShieldCheck size={18} className="text-[#B7F52A]" /> Double vérification</p>
      <p className="text-sm text-slate-400">Pour protéger l'administration, un code à 6 chiffres est envoyé à {user.email}. L'accès reste ouvert 4 heures.</p>
      <button onClick={send} disabled={busy} className={sent ? "mpl-btn-ghost" : "mpl-btn-primary"} data-testid="admin-mfa-send"><Mail size={15} /> {sent ? "Renvoyer un code" : "Recevoir un code"}</button>
      <form onSubmit={verify} className="space-y-3">
        <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="mpl-input font-mono-num tracking-[0.4em]" placeholder="000000" aria-label="Code à 6 chiffres" data-testid="admin-mfa-code" />
        <button disabled={code.length !== 6 || busy} className="mpl-btn-primary" data-testid="admin-mfa-verify">Valider</button>
      </form>
    </section>
  );
}

export default function SuperAdmin() {
  const { tab = "" } = useParams();
  const [elevated, setElevated] = useState(null);
  const check = useCallback(() => { api.get("/superadmin/mfa/status").then(({ data }) => setElevated(data.elevated)).catch(() => setElevated(false)); }, []);
  useEffect(() => { check(); }, [check]);
  const Body = { "": Overview, ligues: LeaguesAdmin, entreprises: CorporateAdmin, organisations: OrganizationsAdmin, contenu: SiteAdmin, apparence: AppearanceAdmin, experiences: ExperiencesAdmin, regie: RegieAdmin, moderation: Moderation, exploitation: Ops }[tab] || Overview;
  return (
    <div data-testid="superadmin-page">
      <PageHeader eyebrow="Superadministration" title="Plateforme" />
      {elevated === null ? <Spinner /> : !elevated ? <MfaGate onDone={() => setElevated(true)} /> : (<>
        <nav className="mpl-scroll-x mb-5 flex gap-2 overflow-x-auto" data-testid="admin-tabs">{TABS.map(([k, l]) => (
          <NavLink key={k} end to={`/admin${k ? `/${k}` : ""}`} data-testid={`admin-tab-${k || "overview"}`}
            className={({ isActive }) => `shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold ${isActive ? "border-[#B7F52A] bg-[#B7F52A] text-[#05070D]" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>{l}</NavLink>))}
          <NavLink to="/admin/synchronisation" data-testid="admin-tab-sync" className="shrink-0 rounded-full border border-white/10 px-4 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 lg:hidden">Synchronisation</NavLink></nav>
        <Body onExpired={() => setElevated(false)} />
      </>)}
    </div>
  );
}

export const useAdminGet = (url, onExpired) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    setError("");
    api.get(url).then(({ data: d }) => setData(d)).catch((e) => { if (e.response?.status === 428) onExpired?.(); else setError(errMsg(e)); });
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return { data, error, load };
};
